import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full rounded-xl border border-forest-deep/15 bg-white px-4 py-3 text-sm text-forest-deep outline-none transition-colors focus:border-forest-primary";
const TOPICS = [
  "India & Global EPR",
  "Solid Waste Management",
  "Bulk Waste Generators",
  "Circular Economy Advisory",
  "Waste Management CSR",
  "Client technology / SaaS walkthrough",
  "A mandate across several practices",
];

export default function ContactForm({ defaultTopic = "", compact = false }) {
  const [params] = useSearchParams();
  const initialTopic = defaultTopic || params.get("topic") || TOPICS[0];
  const [form, setForm] = useState({ topic: initialTopic, name: "", email: "", company: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const resp = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          message: `Topic: ${form.topic}\n\n${form.message}`,
          source: "website_redesign",
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof data.detail === "string" ? data.detail : "The enquiry could not be sent. Please email alik@clymaco.com directly.");
      setResult({ ok: true, text: "Your enquiry has been sent to CLYMACO. We will respond using the details you provided." });
      setForm((f) => ({ ...f, name: "", email: "", company: "", phone: "", message: "" }));
    } catch (err) {
      setResult({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-3xl border border-forest-deep/10 bg-white p-6 shadow-sm sm:p-8" data-testid="contact-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="sm:col-span-2">
          <span className="mb-2 block text-sm font-semibold text-forest-deep">What would you like to discuss?</span>
          <select value={form.topic} onChange={(e) => update("topic", e.target.value)} className={inputCls} data-testid="contact-form-practice-select">
            {TOPICS.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-2 block text-sm font-semibold text-forest-deep">Your name</span>
          <input required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} data-testid="contact-form-name-input" autoComplete="name" />
        </label>
        <label>
          <span className="mb-2 block text-sm font-semibold text-forest-deep">Work email</span>
          <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="contact-form-email-input" autoComplete="email" />
        </label>
        <label>
          <span className="mb-2 block text-sm font-semibold text-forest-deep">Company / organisation</span>
          <input required value={form.company} onChange={(e) => update("company", e.target.value)} className={inputCls} data-testid="contact-form-company-input" autoComplete="organization" />
        </label>
        <label>
          <span className="mb-2 block text-sm font-semibold text-forest-deep">Phone <span className="font-normal text-forest-deep/45">(optional)</span></span>
          <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} data-testid="contact-form-phone-input" autoComplete="tel" />
        </label>
        <label className="sm:col-span-2">
          <span className="mb-2 block text-sm font-semibold text-forest-deep">A little about your project</span>
          <textarea required rows={compact ? 4 : 6} value={form.message} onChange={(e) => update("message", e.target.value)} className={inputCls} data-testid="contact-form-message-input" placeholder="Your organisation, location and the challenge you want to address." />
        </label>
      </div>
      {result && (
        <p className={`mt-4 rounded-xl px-4 py-3 text-sm font-medium ${result.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="contact-form-result">
          {result.text}
        </p>
      )}
      <button type="submit" disabled={busy} className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest-primary px-6 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-forest-rich disabled:opacity-60" data-testid="contact-form-submit-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        Send enquiry
      </button>
      <p className="mt-3 text-xs leading-relaxed text-forest-deep/50">This sends a real enquiry to CLYMACO. You can also email alik@clymaco.com directly.</p>
    </form>
  );
}
